/**
 * Directs the closing animation of an antd modal at a point on the screen - the tree icon the
 * popup belongs to - instead of letting it collapse into its own centre.
 *
 * How it works: a modal closes through `antZoomOut`, which is a plain
 * `transform: scale(1 → 0.2)` (`opacity 1 → 0`) and, unlike the directional variants
 * (`antZoomUp`, `antZoomLeft`, ...), it sets no `transform-origin` in its keyframes. So the
 * origin of the element itself decides where the modal shrinks to, and antd's own "open from the
 * click position" feature relies on the same fact. Setting that origin from the marker's screen
 * position makes the window collapse into the icon that was just closed.
 *
 * It must run right before the modal is closed (`open`/`visible` → false): the leave animation
 * then starts in the next frame and picks the origin up. antd/rc-dialog only ever write
 * `transform-origin` when the modal is prepared to *open*, so nothing overwrites it here.
 *
 * `anchor` is any node inside the modal (a hidden element is enough): the panel that carries the
 * animation - and the class `ant-modal` - is looked up from it, which avoids guessing at the
 * modal's DOM from the outside. Checked against the packages this app installs: rc-dialog's panel
 * is the `div[role="dialog"]` whose class list holds both `ant-modal` and the motion classes
 * (`ant-zoom-leave ant-zoom-leave-active`), and it is the element rc-dialog itself writes
 * `transform-origin` on for antd's "open from the click position" feature.
 */
export function collapseModalAtPoint(
  anchor: HTMLElement | null | undefined,
  point: { x: number; y: number } | null | undefined
): void {
  const panel = anchor?.closest('.ant-modal') as HTMLElement | null;

  if (!panel || !point) {
    return;
  }

  // `transform-origin` is relative to the panel's own box, hence the marker's viewport position
  // minus the panel's own offset on screen.
  const panelRect = panel.getBoundingClientRect();

  panel.style.transformOrigin = `${point.x - panelRect.left}px ${point.y - panelRect.top}px`;
}
